import React from 'react';
import { Trophy } from 'lucide-react';

export default function ScoreRing({ score = 0, potentialScore = 0, grade = 'C', size = 110 }) {
  const R = (size - 16) / 2;
  const circ = 2 * Math.PI * R;
  const pct = Math.min(95, Math.max(0, score)); // Heuristic cap at 95
  const offset = circ - (pct / 100) * circ;
  
  const strokeColor = pct >= 85 
    ? 'rgb(16, 185, 129)' // Emerald-500
    : pct >= 65 
      ? 'rgb(245, 158, 11)' // Amber-500
      : 'rgb(239, 68, 68)';  // Red-500

  const gain = Math.max(0, parseFloat((potentialScore - score).toFixed(1)));

  return (
    <div 
      className="backdrop-blur-md rounded-2xl p-6 flex flex-col md:flex-row items-center gap-6 relative overflow-hidden"
      style={{
        background: 'var(--card-bg)',
        border: '1px solid var(--card-border)',
        boxShadow: 'var(--card-shadow)'
      }}
    >
      <div className="relative shrink-0 flex items-center justify-center">
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ transform: 'rotate(-90deg)' }}>
          <circle cx={size/2} cy={size/2} r={R} fill="none" stroke="var(--divider)" strokeWidth={8} />
          <circle
            cx={size/2} cy={size/2} r={R} fill="none"
            stroke={strokeColor} strokeWidth={8}
            strokeDasharray={circ} strokeDashoffset={offset}
            strokeLinecap="round"
            className="transition-all duration-1000 ease-out"
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-3xl font-black leading-none" style={{ color: 'var(--text-primary)' }}>{score}</span>
          <span className="text-[10px] font-bold uppercase tracking-wider mt-1" style={{ color: 'var(--text-muted)' }}>/ 95 Max</span>
        </div>
      </div>
      
      <div className="space-y-2 flex-1 min-w-0 text-center md:text-left">
        <div className="flex flex-wrap items-center justify-center md:justify-start gap-2">
          <div className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-400 bg-emerald-400/10 px-2 py-0.5 rounded-full uppercase tracking-wider">
            <Trophy className="w-3 h-3" />
            Grade {grade}
          </div>
          {gain > 0 && (
            <div className="inline-flex items-center gap-1 text-[10px] font-bold text-sky-400 bg-sky-400/10 px-2 py-0.5 rounded-full uppercase tracking-wider">
              +{gain} Potential Gain
            </div>
          )}
        </div>
        
        <h2 className="text-lg font-bold truncate" style={{ color: 'var(--text-primary)' }}>
          {score >= 85 ? 'Excellent Profile' : score >= 65 ? 'Optimizable Profile' : 'Weak Profile Structure'}
        </h2>
        
        <div className="flex flex-wrap items-center justify-center md:justify-start gap-4 text-xs mt-1">
          <div style={{ color: 'var(--text-secondary)' }}>
            Current: <span className="font-bold" style={{ color: 'var(--text-primary)' }}>{score}</span>
          </div>
          {gain > 0 && (
            <>
              <div style={{ color: 'var(--text-muted)' }}>➔</div>
              <div className="font-bold rounded px-1.5 py-0.5" style={{ color: 'var(--brand)', background: 'var(--brand-light)', border: '1px solid var(--border-subtle)' }}>
                Potential: {potentialScore}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
